import { isPlatformServer } from '@angular/common';
import {
  Injectable,
  PLATFORM_ID,
  REQUEST,
  TransferState,
  WritableSignal,
  afterNextRender,
  inject,
  makeStateKey,
  signal
} from '@angular/core';
import {
  CHANNEL_MAP,
  CHANNEL_SOURCE,
  ChannelSourceHeader
} from './channel-source.model';
import { Mobile_breakpoint } from '../device-detector/breakpoints';
import { userAccountUrls } from '../../enum/routes';
import { BinanceService } from '../binance/binance.service';
import { GateIoService } from '../gate-io/gate-io.service';
import { GateWayService } from '../gateway.service';
import { UTMData } from '../utm.service';
import { LocalStorageVariables } from '../../enum/localstorage-variables';
import { LocalStorageService } from '../storage/local-storage.service';

@Injectable({
  providedIn: 'root'
})
export class ChannelSourceService {
  private static readonly SSR_CHANNEL_SOURCE_KEY =
    makeStateKey<CHANNEL_SOURCE>('ssr-channel-source');

  private binanceService = inject(BinanceService);
  private gateIOService = inject(GateIoService);
  private gatewayService = inject(GateWayService);
  private localStorageService = inject(LocalStorageService);
  private transferState = inject(TransferState);
  private request = inject(REQUEST, { optional: true });

  private source!: CHANNEL_SOURCE;

  get channelSource(): CHANNEL_SOURCE {
    return this.source;
  }
  source_: WritableSignal<CHANNEL_SOURCE | null> = signal(null);
  set channelSource(value: CHANNEL_SOURCE) {
    this.applySource(value);
  }
  private EXPIRATION_DAYS = 7;
  platformId = inject(PLATFORM_ID);
  constructor() {
    if (isPlatformServer(this.platformId)) {
      this.initializeServerSourceFromRequest();
      return;
    }

    const serverDetectedSource = this.transferState.get(
      ChannelSourceService.SSR_CHANNEL_SOURCE_KEY,
      null
    );
    if (serverDetectedSource) {
      this.applySource(serverDetectedSource);
      this.transferState.remove(ChannelSourceService.SSR_CHANNEL_SOURCE_KEY);
    }

    this.binanceService.refreshBridgeState();
    this.setSource();
    afterNextRender(() => {
      this.initializeClientEnvironment();
      queueMicrotask(() => this.initializeClientEnvironment());
      setTimeout(() => this.initializeClientEnvironment(), 0);
      setTimeout(() => this.initializeClientEnvironment(), 120);
    });
  }

  private initializeServerSourceFromRequest(): void {
    const sourceFromQueryParams = this.detectSourceFromRequestQueryParams(
      this.request?.url
    );
    const userAgent = this.request?.headers.get('user-agent');
    const detectedSource =
      sourceFromQueryParams ?? this.detectSourceFromUserAgent(userAgent);
    this.applySource(detectedSource);
    this.transferState.set(
      ChannelSourceService.SSR_CHANNEL_SOURCE_KEY,
      detectedSource
    );
  }

  private detectSourceFromRequestQueryParams(
    requestUrl: string | undefined
  ): CHANNEL_SOURCE | null {
    if (!requestUrl) {
      return null;
    }

    const baseUrl = 'http://localhost';
    let parsedUrl: URL;
    try {
      parsedUrl = new URL(requestUrl, baseUrl);
    } catch {
      return null;
    }

    const querySourceCandidates = [
      parsedUrl.searchParams.get('utm_source'),
      parsedUrl.searchParams.get('utm_campaign'),
      parsedUrl.searchParams.get('utm_medium'),
      parsedUrl.searchParams.get('source'),
      parsedUrl.searchParams.get('channel')
    ]
      .filter((value): value is string => !!value)
      .map((value) => value.toLowerCase());

    for (const value of querySourceCandidates) {
      const mappedSource = this.mapQueryValueToChannelSource(value);
      if (mappedSource) {
        return mappedSource;
      }
    }

    return null;
  }

  private mapQueryValueToChannelSource(value: string): CHANNEL_SOURCE | null {
    const normalizedValue = value.toLowerCase();

    if (normalizedValue.includes('binance') || normalizedValue === 'bn') {
      return CHANNEL_SOURCE.BINANCE;
    }

    if (
      normalizedValue.includes('gate') ||
      normalizedValue.includes('gateio') ||
      normalizedValue.includes('gate-io')
    ) {
      return CHANNEL_SOURCE.GATEIO;
    }

    if (normalizedValue.includes('kucoin')) {
      return CHANNEL_SOURCE.KUCOIN;
    }

    if (normalizedValue.includes('bybit')) {
      return CHANNEL_SOURCE.BYBIT;
    }

    for (const key of Object.keys(CHANNEL_MAP)) {
      if (normalizedValue.includes(key)) {
        return CHANNEL_MAP[key].slug;
      }
    }

    return null;
  }

  private detectSourceFromUserAgent(
    userAgent: string | null | undefined
  ): CHANNEL_SOURCE {
    if (this.binanceService.isBinanceUserAgent(userAgent)) {
      return CHANNEL_SOURCE.BINANCE;
    }

    if (this.gateIOService.isGateIoUserAgent(userAgent)) {
      return CHANNEL_SOURCE.GATEIO;
    }

    return CHANNEL_SOURCE.DESKTOP;
  }

  /**
   * Re-detects channel from the real browser environment after SSR / hydration.
   * Safe to call multiple times (e.g. when the Binance bridge appears late).
   */
  initializeClientEnvironment(): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }
    this.binanceService.refreshBridgeState();
    this.setSource();
  }

  private applySource(value: CHANNEL_SOURCE): void {
    this.source = value;
    this.source_.set(value);
  }

  setSource() {
    const isServer = isPlatformServer(this.platformId);

    if (isServer) {
      this.applySource(CHANNEL_SOURCE.DESKTOP);
      return;
    }

    if (this.binanceService.isBinance) {
      this.applySource(CHANNEL_SOURCE.BINANCE);
      return;
    }

    if (this.gateIOService.isGateIo()) {
      this.applySource(CHANNEL_SOURCE.GATEIO);
      return;
    }

    if (this.gatewayService.isKucoin()) {
      this.applySource(CHANNEL_SOURCE.KUCOIN);
      return;
    }

    if (this.gatewayService.isByBit()) {
      this.applySource(CHANNEL_SOURCE.BYBIT);
      return;
    }

    // Preserve an existing non-default source resolved from SSR/UTM.
    if (
      this.source &&
      this.source !== CHANNEL_SOURCE.DESKTOP &&
      this.source !== CHANNEL_SOURCE.MOBILE
    ) {
      return;
    }

    this.setDefaultSource();
  }

  setDefaultSource() {
    const isMobile = window?.innerWidth < Mobile_breakpoint;
    this.applySource(
      isMobile ? CHANNEL_SOURCE.MOBILE : CHANNEL_SOURCE.DESKTOP
    );
  }

  private isUserAccountUrl(route: string) {
    return Object.values(userAccountUrls).some((url) => route.includes(url));
  }

  channelSourceHeader(routeUrl: string): ChannelSourceHeader {
    if (this.localStorageService.getItem(LocalStorageVariables['utm-data'])) {
      const utmData: UTMData = JSON.parse(
        this.localStorageService.getItem(LocalStorageVariables['utm-data'])
      );
      if (utmData) {
        const diffDays =
          (Date.now() - utmData.timestamp) / (1000 * 60 * 60 * 24);

        if (diffDays > this.EXPIRATION_DAYS) {
          this.localStorageService.deleteItem(
            LocalStorageVariables['utm-data']
          );
        } else {
          const detectedFromUtm = this.detectSourceFromUtmData(utmData);
          if (detectedFromUtm) {
            this.applySource(detectedFromUtm);
          }
        }
      }
    }
    return {
      ...(!this.isUserAccountUrl(routeUrl) && {
        'X-CHANNEL-SOURCE': this.source
      })
    };
  }

  private detectSourceFromUtmData(utmData: UTMData): CHANNEL_SOURCE | null {
    const candidates = [utmData.utm_source, utmData.utm_medium, utmData.channel]
      .filter((value): value is string => !!value)
      .map((value) => value.toLowerCase());

    for (const value of candidates) {
      const mappedSource = this.mapQueryValueToChannelSource(value);
      if (mappedSource) {
        return mappedSource;
      }
    }

    return null;
  }
}
